import { DeepLinkType, ExploreCard } from "pages/api/deep-links/types";
import { useEffect } from "react";
import { SearchState } from "src/search/types";
import { useSearch } from "src/search/useSearchContext";
import { EntityPreview } from "src/search/components/EntityPreview";
import { unserializeParams } from "src/search/lib/params-state-reducer";
import { ContentViews, useContentView } from "../explore/lib/useContentView";
import { ResponseCanvas } from "../explore/ResponseCanvas";
import { TITLEBAR_HEIGHT } from "./QuestionTitlebar";
import { getCorrectedContentView } from "./lib";

export function QuestionViewer({
  card,
  cardId: cardIdForLogging, // renamed for logging purposes
  state,
  pivoting,
  mergeModifiedState,
}: {
  card: ExploreCard;
  cardId: string;
  state: SearchState;
  pivoting: boolean;
  mergeModifiedState(state: SearchState): void;
}) {
  if (card.type !== DeepLinkType.EXPLORE)
    throw new Error("expected question card");

  // We create 2 search contexts.
  // 1. `search` is used for rendering
  // 2. `shadow` silently captures changes
  //
  // We swap out the search.params.dispath for the
  // `shadow` one. When the card tries to update itself
  // it updates the shadow one instead.
  //
  // So we watch for changes to the shadow state,
  // the merge that into the modified state, letting
  // the updated card depend on the top-down
  // approach continue as normal.

  const search = useSearch(state);
  const shadow = useSearch(state);
  const contentView = getCorrectedContentView(card.state.viz, pivoting);
  const view = useContentView(search, contentView);

  search.params.dispatch = shadow.params.dispatch;
  const curr = search.params.serialized;
  const next = shadow.params.serialized;

  useEffect(() => {
    if (curr !== next) {
      const state = unserializeParams(next);
      if (state) {
        mergeModifiedState(state);
      }
    }
  }, [curr, next, mergeModifiedState, cardIdForLogging]);

  return (
    <>
      <div
        style={{
          height: `calc(100% - ${TITLEBAR_HEIGHT}px)`,
          overflow: "visible",
          borderRadius: 14,
          padding: 4,
        }}
      >
        <ResponseCanvas
          onRefuse={() => view.set(ContentViews.dataTable)}
          context={{ ...search, view, title: card.title }}
        />
      </div>
      <EntityPreview context={search} />
    </>
  );
}
